<script setup>
import { computed } from 'vue';
import axios from 'axios';
import { useForm } from 'vee-validate';
import { useUserForm } from 'src/composables/useUserForm';
import { useStore } from 'vuex';
import { useAnalytics } from 'src/composables/analytics';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  /**
   * One of the available contact types:
   * - bce
   * - psr
   * - account_manager
   * - customer_service
   * - data_protection_officer
   * - contact_block ("standard" contact form)
   */
  contactType: {
    type: String,
    required: true
  },
  additionalData: {
    type: Object,
    required: true,
  },
  inModal: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['finished']);

const store = useStore();
const { t } = useI18n();

const user = computed(() => store.state.user.activeUser);
const regionSelectionNecessary = computed(() => props.contactType === 'bce' && (!user.value || !user.value.region));

const { trackEvent } = useAnalytics();

const topics = computed(() => {
  if (props.additionalData
    && props.additionalData.hasOwnProperty('topics')
    && Array.isArray(props.additionalData.topics)
    && props.additionalData.topics.length > 0) {
    return props.additionalData.topics;
  }

  return null;
});

const hasTopics = computed(() => props.contactType === 'contact_block' && topics.value !== null);

const { handleSubmit, resetForm, values: formValues } = useForm({
  initialValues: {
    firstName: user.value && user.value.firstName ? user.value.firstName : null,
    lastName: user.value && user.value.lastName ? user.value.lastName : null,
    email: user.value && user.value.email ? user.value.email : null,
    company: user.value && user.value.company ? user.value.company : null,
    country: user.value && user.value.country ? user.value.country : null,
    topic: hasTopics.value ? topics.value[0] : null,
  }
});

const {
  isValidated,
  contactRegions,
  isSending,
  formSecurityToken,
  fullRegionLabel,
  message,
  hasMessage,
  consentHintNecessary,
  links,
} = useUserForm({
  formValues,
  regionSelectionNecessary,
});

const industries = computed(() => store.state.generic.industries);

const countrySelectionNecessary = computed(() => ['bce', 'contact_block', 'psr', 'account_manager', 'customer_service'].includes(props.contactType));
const industrySelectionNecessary = computed(() => ['bce', 'contact_block'].includes(props.contactType));

const industryOptions = computed(() => Object.fromEntries(industries.value.map((r) => [r.code, r.title])));

const topicSelectOptions = computed(() => {
  const options = {};

  if (topics.value) {
    topics.value.forEach((topic) => {
      options[topic.id] = topic.title;
    });
  }

  return options;
});

const contactRegionOptions = computed(() => contactRegions.value.map((contactRegion) => ({
  ...contactRegion,
  value: contactRegion.code
})));

const submit = handleSubmit(async (values) => {
  isSending.value = true;

  const data = {
    message: values.message,
    ...props.additionalData,
    formSecurityToken: formSecurityToken.value,
    contactType: props.contactType
  };

  ['firstName', 'lastName', 'company', 'country', 'email', 'region', 'subRegion'].forEach((f) => {
    data[f] = user.value && user.value[f] ? user.value[f] : values[f];
  });

  if (values.industry) {
    data.industry = values.industry;
  }

  if (isValidated.value) {
    if (props.contactType === 'customer_service') {
      data.recipient = user.value.customerService.email;
    } else if (props.contactType === 'account_manager') {
      data.recipient = user.value.accountManager.email;
    }
  }

  if (hasTopics.value && values.selectedTopic) {
    data.recipient = values.selectedTopic.recipient;
  }

  try {
    await axios.post(`${location.origin}/api/v1/message/contactForm`, data);
    emit('finished');

    trackEvent(
      'contact-form',
      props.contactType,
      {
        region: fullRegionLabel.value
      }
    );

    resetForm({
      firstName: user.value && user.value.firstName ? user.value.firstName : null,
      lastName: user.value && user.value.lastName ? user.value.lastName : null,
      email: user.value && user.value.email ? user.value.email : null,
      company: user.value && user.value.company ? user.value.company : null,
      country: user.value && user.value.country ? user.value.country : null,
      region: null,
      subRegion: null,
      industry: null,
      message: null
    });

    isSending.value = false;

    // Show a success message if not rendered inside a modal
    if (!props.inModal) {
      message.value = {
        type: 'success',
        content: t('contactForm.finishedMessage')
      };
    }
  } catch (e) {
    message.value = {
      type: 'error',
      content: e.response?.data?.message ?? e.message
    };
  }

  isSending.value = false;
});
</script>

<template>
  <div class="ce-modalContent">
    <alert
      class="mb3"
      v-if="hasMessage"
      :message="message.content"
      :type="message.type"
      @close="message = {}" />

    <form
      @submit="submit">
      <div
        class="ce-contactForm-userInfo">
        <div class="flex flex-wrap">
          <validated-input
            class="col-12 xs-col-6 xs-pr3"
            name="firstName"
            :disabled="!!(user && user.firstName)"
            :label="$t('contactForm.firstName')"
            rules="required" />

          <validated-input
            class="col-12 xs-col-6 xs-pl3"
            name="lastName"
            :disabled="!!(user && user.lastName)"
            :label="$t('contactForm.lastName')"
            rules="required" />
        </div>

        <validated-input
          name="email"
          :disabled="!!(user && user.email)"
          :label="$t('contactForm.email')"
          rules="required|email" />

        <validated-input
          name="company"
          :disabled="!!(user && user.company)"
          :label="$t('contactForm.company')"
          rules="required" />

        <country-select
          v-if="countrySelectionNecessary"
          name="country"
          :disabled="!!(user && user.country)"
          :label="$t('countrySelect.label')"
          rules="required" />

        <validated-select
          v-if="industrySelectionNecessary"
          name="industry"
          :label="$t('contactForm.industry')"
          :options="industryOptions"
          rules="required"
          show-other-option />
      </div>

      <validated-select
        v-if="hasTopics"
        name="topic"
        :label="'Topics'"
        :options="topicSelectOptions"
        :use-key-only-value="false" />

      <div>
        <validated-select
          v-if="regionSelectionNecessary"
          name="region"
          :label="$t('contactForm.region')"
          :options="contactRegionOptions"
          rules="required"
          :use-key-only-value="false"
        />

        <validated-select
          v-if="regionSelectionNecessary && formValues.region && formValues.region.subRegions"
          name="subRegion"
          :label="$t('contactForm.subRegion')"
          :options="formValues.region.subRegions"
          rules="required"
          :use-key-only-value="false" />

        <validated-input
          name="message"
          type="textarea"
          :label="$t('contactForm.message')"
          rules="required"
        />
      </div>

      <div
        v-if="consentHintNecessary"
        class="ms-1 line-height-4">
        <i18n-t
          keypath="contactForm.consentHint"
          tag="p"
          scope="global">
          <a
            :href="links.sunChemicalDataProtectionPage ?? links.dataProtectionPage"
            rel="noopener noreferrer">
            {{ $t('contactForm.dataProtectionPage') }}</a>
        </i18n-t>
      </div>

      <div class="mt4">
        <button
          class="ce-btn ce-btn-light ce-btn--fullWidth"
          type="submit">
          <template v-if="isSending">
            <fa-icon
              icon="spinner"
              spin />
          </template>
          <template v-else>
            {{ $t('contactForm.submitCta') }}
          </template>
        </button>
      </div>
    </form>
  </div>
</template>
